import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, Server, Users, FileCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/Button';
import styles from './legal.module.css';

export default function SecurityPage() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    document.body.style.overflow = 'auto';
    return () => { if (root) { root.style.overflow = ''; root.style.height = ''; } document.body.style.overflow = ''; };
  }, []);

  return (
    <div className={styles.container}>
      <>
        <title>Security | Tasking.tech</title>
        <meta
          name="description"
          content="Security at Tasking.tech - Enterprise-grade agent isolation, encryption, audit trails, and compliance controls"
        />
      </>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.logoIcon}
            />
            <span className={styles.logoText}>
              <span>tasking</span>
              <span className={styles.techSuffix}>.tech</span>
            </span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </Button>
        </div>
      </nav>

      <main className={styles.content}>
        <h1>Security at Tasking.tech</h1>
        <p className={styles.lastUpdated}>Last updated: March 29, 2026</p>

        <section className={styles.section}>
          <h2>1. Our Security Commitment</h2>
          <p>
            Tasking.tech is an AI-powered agentic development platform trusted by teams to build,
            deploy, and manage software with autonomous AI agents. Security is foundational to
            everything we build. We implement defense-in-depth across every layer of the platform
            to protect your code, data, credentials, and agent operations.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Infrastructure Security</h2>
          <h3>2.1 Cloud Architecture</h3>
          <p>
            Tasking.tech runs on Cloudflare's global edge network, leveraging enterprise-grade
            infrastructure with built-in DDoS protection, TLS termination, and geographic
            distribution across 300+ data centers worldwide.
          </p>
          <ul>
            <li><strong>Compute:</strong> Cloudflare Workers (V8 isolate sandboxing per request)</li>
            <li><strong>Database:</strong> Cloudflare D1 (SQLite at the edge, encrypted at rest)</li>
            <li><strong>Storage:</strong> Cloudflare R2 (S3-compatible object storage, encrypted at rest)</li>
            <li><strong>KV:</strong> Cloudflare KV (distributed key-value store for session and asset data)</li>
            <li><strong>Durable Objects:</strong> Strongly consistent state for real-time collaboration sessions</li>
          </ul>

          <h3>2.2 Network Security</h3>
          <ul>
            <li>All traffic encrypted in transit via TLS 1.3</li>
            <li>HSTS enforced with preload (max-age=31536000, includeSubDomains)</li>
            <li>Cloudflare WAF and bot management active on all endpoints</li>
            <li>Per-IP rate limiting on all API endpoints with endpoint-specific thresholds</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>3. Authentication & Access Control</h2>
          <h3>3.1 Authentication Methods</h3>
          <ul>
            <li><strong>OAuth 2.0 + PKCE:</strong> Google, GitHub, Microsoft, Auth0, Okta</li>
            <li><strong>Password Authentication:</strong> bcrypt-hashed with brute-force protection (lockout after 5 failed attempts)</li>
            <li><strong>Two-Factor Authentication (TOTP):</strong> Optional 2FA via authenticator apps</li>
            <li><strong>Single Sign-On (SSO):</strong> Per-tenant Okta and Auth0 configuration for enterprise customers</li>
          </ul>

          <h3>3.2 Session Management</h3>
          <ul>
            <li>JWT-based sessions with HS256 signing</li>
            <li>HttpOnly, Secure, SameSite=Lax cookies</li>
            <li>7-day session expiry with rolling timestamps</li>
            <li>Automatic session cleanup of expired tokens</li>
          </ul>

          <h3>3.3 Multi-Tenancy & RBAC</h3>
          <p>
            Tasking.tech supports full multi-tenancy with role-based access control.
            Tenant isolation is enforced at the database query level with per-tenant
            filtering on all data access paths.
          </p>
          <ul>
            <li><strong>Roles:</strong> Owner, Admin, Member, Viewer, Billing</li>
            <li><strong>Session Permissions:</strong> Edit, View, None (per-participant granularity)</li>
            <li><strong>File Permissions:</strong> Per-file access control within collaboration sessions</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>4. Data Protection</h2>
          <h3>4.1 Encryption</h3>
          <ul>
            <li><strong>In Transit:</strong> TLS 1.3 on all connections</li>
            <li><strong>At Rest:</strong> Cloudflare-managed encryption for D1, R2, and KV storage</li>
            <li><strong>Credentials:</strong> AES-256-GCM encryption with per-deployment master keys and key rotation support</li>
            <li><strong>Integration Secrets:</strong> Encrypted before storage, never logged or exposed in API responses</li>
          </ul>

          <h3>4.2 Data Handling</h3>
          <ul>
            <li>API responses include Cache-Control: no-store for sensitive endpoints</li>
            <li>Credential values are scrubbed from all AI output before delivery to clients</li>
            <li>API keys, tokens, and secrets are pattern-matched and redacted in real-time</li>
            <li>No sensitive data in URL parameters</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>5. AI Agent Security</h2>
          <h3>5.1 Prompt Injection Protection</h3>
          <p>
            All user input is processed through multi-layer guardrails before reaching AI models:
          </p>
          <ul>
            <li>Social engineering detection (role impersonation, instruction override, jailbreak attempts)</li>
            <li>Cross-user access prevention (user enumeration, data access across accounts)</li>
            <li>Credential extraction blocking (secret reveal, decryption, dump requests)</li>
            <li>Data exfiltration prevention (outbound transfer to untrusted endpoints)</li>
            <li>System probing detection (prompt extraction, environment variable access, SQL injection)</li>
            <li>XML/HTML injection tag stripping and instruction boundary enforcement</li>
          </ul>

          <h3>5.2 Tool Execution Safety</h3>
          <ul>
            <li>Webhook URLs validated against domain allowlist (SSRF prevention)</li>
            <li>Private IP and localhost blocking on all outbound requests</li>
            <li>Path traversal prevention on workflow triggers</li>
            <li>Task status and input validation before database writes</li>
            <li>Credential template variables resolved server-side (never exposed to LLM)</li>
          </ul>

          <h3>5.3 Output Sanitization</h3>
          <ul>
            <li>All AI output scrubbed for leaked API keys, tokens, and secrets</li>
            <li>Environment variable names and values redacted from responses</li>
            <li>Internal protocol markers and debug information stripped</li>
            <li>DOMPurify sanitization on all rendered HTML content</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>6. Application Security</h2>
          <h3>6.1 Security Headers</h3>
          <ul>
            <li>Strict-Transport-Security (HSTS with preload)</li>
            <li>X-Content-Type-Options: nosniff</li>
            <li>X-XSS-Protection: 1; mode=block</li>
            <li>Referrer-Policy: strict-origin-when-cross-origin</li>
            <li>Permissions-Policy: camera=(), microphone=(self), geolocation=()</li>
            <li>Cross-Origin-Opener-Policy: same-origin</li>
            <li>Cross-Origin-Embedder-Policy: credentialless</li>
            <li>Content-Security-Policy with frame-ancestors restriction</li>
          </ul>

          <h3>6.2 CSRF Protection</h3>
          <p>
            All state-changing API requests (POST, PATCH, DELETE) are validated against
            trusted origins. SameSite=Lax cookies provide baseline protection, with
            server-side Origin/Referer validation as defense-in-depth.
          </p>

          <h3>6.3 Input Validation</h3>
          <ul>
            <li>Zod schema validation on all API request bodies</li>
            <li>Kysely ORM with parameterized queries (SQL injection prevention)</li>
            <li>File ownership verification on all CRUD operations</li>
            <li>Session and resource ownership checks (IDOR prevention)</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Monitoring & Audit</h2>
          <ul>
            <li><strong>Audit Logging:</strong> Platform-wide audit table with severity levels tracking all security-relevant events</li>
            <li><strong>Rate Limiting:</strong> Per-IP, per-endpoint sliding window with configurable thresholds</li>
            <li><strong>Login Monitoring:</strong> Failed attempt tracking with automatic lockout</li>
            <li><strong>CI/CD Security:</strong> Automated Gitleaks secret detection, CodeQL analysis, Semgrep SAST, and dependency vulnerability scanning</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>8. Service Level Agreement (SLA)</h2>
          <h3>8.1 Uptime Commitment</h3>
          <p>
            Tasking.tech targets <strong>99.9% uptime</strong> for all production services,
            measured monthly. This equates to no more than 43 minutes of unplanned
            downtime per month.
          </p>
          <ul>
            <li><strong>Platform API:</strong> 99.9% availability</li>
            <li><strong>Real-time collaboration:</strong> 99.9% availability</li>
            <li><strong>Autonomous task execution:</strong> 99.9% availability (cron scheduler)</li>
            <li><strong>Asset storage (R2/KV):</strong> 99.99% durability (Cloudflare SLA)</li>
          </ul>

          <h3>8.2 Scheduled Maintenance</h3>
          <p>
            Maintenance windows are announced 48 hours in advance via status page and
            email notification. Zero-downtime deployments are used for routine updates —
            Cloudflare Workers deploy atomically with no service interruption.
          </p>

          <h3>8.3 Support Response Times</h3>
          <ul>
            <li><strong>Critical (service down):</strong> 1-hour initial response</li>
            <li><strong>High (feature degraded):</strong> 4-hour initial response</li>
            <li><strong>Medium (non-blocking issue):</strong> 1 business day</li>
            <li><strong>Low (question/enhancement):</strong> 2 business days</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>9. Incident Response</h2>
          <h3>9.1 Response Phases</h3>
          <ol>
            <li><strong>Detection:</strong> Automated monitoring via Cloudflare analytics, error rate alerts, and audit log anomaly detection</li>
            <li><strong>Triage:</strong> Severity classification (Critical/High/Medium/Low) within 15 minutes of detection</li>
            <li><strong>Containment:</strong> Isolate affected systems, revoke compromised credentials, enable enhanced logging</li>
            <li><strong>Eradication:</strong> Root cause analysis, patch deployment, configuration correction</li>
            <li><strong>Recovery:</strong> Service restoration, monitoring validation, stakeholder notification</li>
            <li><strong>Post-Incident:</strong> Incident report within 72 hours, lessons learned, control improvements</li>
          </ol>

          <h3>9.2 Communication</h3>
          <ul>
            <li>Affected customers notified within 24 hours of confirmed data breach</li>
            <li>Status page updated in real-time during active incidents</li>
            <li>Post-incident report shared with affected parties</li>
          </ul>

          <h3>9.3 Escalation</h3>
          <ul>
            <li><strong>Critical:</strong> Engineering lead + security team immediately</li>
            <li><strong>High:</strong> Engineering lead within 1 hour</li>
            <li><strong>Medium:</strong> Assigned engineer within 4 hours</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>10. Data Classification & Handling</h2>
          <h3>10.1 Classification Levels</h3>
          <ul>
            <li><strong>Confidential:</strong> Credentials, API keys, encryption keys, session tokens, user passwords — AES-256-GCM encrypted, never logged, scrubbed from AI output</li>
            <li><strong>Sensitive:</strong> User email, display name, OAuth tokens, billing information — encrypted in transit, access-controlled, audit-logged</li>
            <li><strong>Internal:</strong> Session content, file data, task descriptions, chat messages — encrypted at rest (Cloudflare-managed), tenant-isolated</li>
            <li><strong>Public:</strong> Published sites (vibes), landing page content, documentation — publicly accessible by design</li>
          </ul>

          <h3>10.2 Data Retention</h3>
          <ul>
            <li><strong>Sessions:</strong> Retained until user deletion</li>
            <li><strong>Chat messages:</strong> Retained until session deletion</li>
            <li><strong>Audit logs:</strong> Retained for 12 months</li>
            <li><strong>Authentication sessions:</strong> 7-day expiry with automatic cleanup</li>
            <li><strong>OAuth states:</strong> 5-minute expiry, single-use, auto-deleted</li>
          </ul>

          <h3>10.3 Data Deletion</h3>
          <p>
            Users can delete their data at any time. Account deletion removes all
            associated sessions, files, credentials, tasks, and chat history.
            Deletion is propagated to all storage layers (D1, R2, KV) within 24 hours.
          </p>
        </section>

        <section className={styles.section}>
          <h2>11. Change Management</h2>
          <ul>
            <li><strong>Code Review:</strong> All changes require pull request review before merge</li>
            <li><strong>CI/CD Pipeline:</strong> Automated linting, security scanning (Gitleaks, CodeQL, Semgrep), and type checking on every PR</li>
            <li><strong>Deployment:</strong> Zero-downtime atomic deployments via Cloudflare Workers</li>
            <li><strong>Rollback:</strong> Instant rollback via Cloudflare version management</li>
            <li><strong>Database Migrations:</strong> Sequential numbered migrations with rollback support</li>
            <li><strong>Secrets Management:</strong> Environment-level secrets via Cloudflare dashboard, never in code</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>12. Risk Assessment</h2>
          <h3>12.1 Risk Assessment Process</h3>
          <ul>
            <li><strong>Frequency:</strong> Quarterly formal risk assessments, continuous monitoring</li>
            <li><strong>Scope:</strong> All systems processing, storing, or transmitting user data</li>
            <li><strong>Methodology:</strong> Likelihood x Impact matrix (Low/Medium/High/Critical)</li>
            <li><strong>Risk register:</strong> Maintained with owner assignments and treatment plans</li>
            <li><strong>Acceptance criteria:</strong> Residual risk approved by security lead</li>
          </ul>

          <h3>12.2 Threat Modeling</h3>
          <ul>
            <li>STRIDE methodology for new features and integrations</li>
            <li>Attack surface analysis for AI agent operations</li>
            <li>Prompt injection threat modeling for LLM interactions</li>
            <li>Third-party integration risk analysis</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>13. Vendor Risk Management</h2>
          <ul>
            <li><strong>Assessment process:</strong> Security questionnaire, SOC 2 report review, and data processing agreement for all vendors handling user data</li>
            <li><strong>Annual reviews:</strong> All critical vendors reassessed annually</li>
            <li><strong>Data processing agreements:</strong> In place with all data processors</li>
          </ul>
          <h3>13.1 Critical Vendors</h3>
          <ul>
            <li><strong>Cloudflare:</strong> Infrastructure (Workers, D1, R2, KV) — SOC 2 Type II certified</li>
            <li><strong>Anthropic:</strong> AI model provider — SOC 2 Type II certified</li>
            <li><strong>LiveKit:</strong> WebRTC infrastructure — SOC 2 Type II certified</li>
            <li><strong>ModelsLab:</strong> Media generation APIs — assessed annually</li>
            <li><strong>Stripe:</strong> Payment processing — PCI DSS Level 1 certified</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>14. Business Continuity & Disaster Recovery</h2>
          <h3>14.1 Recovery Objectives</h3>
          <ul>
            <li><strong>RTO (Recovery Time Objective):</strong> 4 hours for critical services</li>
            <li><strong>RPO (Recovery Point Objective):</strong> 1 hour for user data</li>
          </ul>

          <h3>14.2 Infrastructure Resilience</h3>
          <ul>
            <li><strong>Cloudflare Workers:</strong> Automatic failover across 300+ data centers — no single point of failure</li>
            <li><strong>D1 Database:</strong> Automatic replication with point-in-time recovery</li>
            <li><strong>R2 Storage:</strong> 99.999999999% (11 nines) durability — data replicated across multiple facilities</li>
            <li><strong>Zero-downtime deployments:</strong> Atomic Worker updates with instant rollback</li>
          </ul>

          <h3>14.3 Communication Plan</h3>
          <ul>
            <li>Status page updated within 15 minutes of incident detection</li>
            <li>Customer notification via email within 24 hours of confirmed impact</li>
            <li>Post-incident report published within 72 hours</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>15. Employee Security</h2>
          <ul>
            <li><strong>Security awareness training:</strong> Annual training for all team members covering phishing, social engineering, secure coding, and data handling</li>
            <li><strong>Acceptable use policy:</strong> Defines permitted use of company systems and data</li>
            <li><strong>Background checks:</strong> Conducted for all employees with access to production data or customer information</li>
            <li><strong>Principle of least privilege:</strong> Access granted only as needed for job function</li>
            <li><strong>Access reviews:</strong> Quarterly review of all access permissions, immediate revocation on role change or departure</li>
            <li><strong>Secure development:</strong> Developers trained on OWASP Top 10, secure coding practices, and code review requirements</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>16. Compliance & Certifications</h2>
          <p>
            Tasking.tech is actively working toward formal compliance certifications:
          </p>
          <ul>
            <li><strong>SOC 2 Type II:</strong> In progress — security controls documented and operational</li>
            <li><strong>GDPR:</strong> Data processing compliant with EU regulations. Data subject rights (access, rectification, erasure, portability) supported. No cross-border data transfer without adequate protections.</li>
            <li><strong>CCPA:</strong> California consumer privacy rights supported. Users can request data access, deletion, and opt-out of data sale (we do not sell data).</li>
          </ul>
          <p>
            For detailed compliance documentation or to request our security questionnaire,
            contact <a href="mailto:security@tasking.tech">security@tasking.tech</a>.
          </p>
        </section>

        <section className={styles.section}>
          <h2>17. Security Policies</h2>
          <p>
            Detailed policy documents are available at{' '}
            <Link to="/policies">tasking.tech/policies</Link>, including:
          </p>
          <ul>
            <li>Acceptable Use Policy</li>
            <li>Confidentiality Agreement</li>
            <li>Security Training Program</li>
            <li>Incident Response Plan</li>
            <li>TSP Security Controls</li>
            <li>Access Review Template</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>18. Vulnerability Disclosure</h2>
          <p>
            We welcome responsible security research. If you discover a vulnerability in
            Tasking.tech, please report it to{' '}
            <a href="mailto:security@tasking.tech">security@tasking.tech</a>.
          </p>
          <ul>
            <li>Acknowledgment within 48 hours</li>
            <li>Severity assessment within 5 business days</li>
            <li>Critical vulnerabilities patched within 24 hours</li>
            <li>Credit given to researchers (with consent) in our security advisories</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>19. Contact</h2>
          <p>
            For security inquiries, incident reports, or compliance documentation requests:
          </p>
          <ul>
            <li><strong>Security:</strong> <a href="mailto:security@tasking.tech">security@tasking.tech</a></li>
            <li><strong>Privacy:</strong> <a href="mailto:privacy@tasking.tech">privacy@tasking.tech</a></li>
            <li><strong>General:</strong> <a href="mailto:support@tasking.tech">support@tasking.tech</a></li>
          </ul>
        </section>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img
              src="/logo.png"
              alt="Tasking.tech"
              className={styles.footerLogo}
            />
            <span>
              tasking<span className={styles.techSuffix}>.tech</span>
            </span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/security">Security</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <Link to="/">Home</Link>
          </div>
          <span className={styles.copyright}>© 2026 Tasking.tech. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
